import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { translations } from "./translations";

const I18nContext = createContext(null);

const LOCALES = ["en", "es", "ja"];

function getInitialLocale() {
  if (typeof window === "undefined") return "en";
  try {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get("lang");
    if (fromQuery && LOCALES.includes(fromQuery)) return fromQuery;
    const fromStorage = window.localStorage.getItem("al_locale");
    if (fromStorage && LOCALES.includes(fromStorage)) return fromStorage;
  } catch (_) {
    /* ignore */
  }
  // Always default to English. No browser-language detection.
  return "en";
}

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(getInitialLocale);

  const setLocale = useCallback((l) => {
    if (!LOCALES.includes(l)) return;
    setLocaleState(l);
    if (typeof window !== "undefined") {
      window.localStorage.setItem("al_locale", l);
      const url = new URL(window.location.href);
      if (l === "en") url.searchParams.delete("lang");
      else url.searchParams.set("lang", l);
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  // Sync <html lang> and meta description with locale
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.lang = locale;
    const t = translations[locale];
    const meta = document.querySelector('meta[name="description"]');
    if (meta && t?.footer?.definition) meta.setAttribute("content", t.footer.definition);
    document.title =
      locale === "es"
        ? "Longevidad Auditiva — la práctica de proteger cómo escuchamos"
        : locale === "ja"
        ? "聴覚の長寿 — 聴き方を守るプラクティス"
        : "Auditory Longevity — the practice of protecting how we listen";
  }, [locale]);

  const t = useMemo(() => translations[locale] || translations.en, [locale]);

  return (
    <I18nContext.Provider value={{ locale, setLocale, t, locales: LOCALES }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}
